/**
 * Utilities for retrieving and shaping course membership data via LTI NRPS.
 * The exported function performs only data access and normalization; UI state
 * such as loading indicators and alerts should be controlled by callers.
 * @module fetchMembers
 */

/**
 * @typedef {Object} MemberDetails
 * @property {string} name
 * @property {string} email
 * @property {string} [first_name]
 * @property {string} [last_name]
 * @property {string[]} roles
 * @property {string} [id]
 * @property {string} [login_id]
 * @property {string|null} [sis_integration_id]
 * @property {string|null} [sis_source_id]
 * @property {string[]|null} [membership_roles] - Canvas membership roles (normalized to array when available)
 * @property {string[]|null} [canvas_membership_roles] - Alias for membership_roles for downstream consumers
 */

/**
 * Fetch the roster via NRPS and return normalised member details.
 *
 * - Filters out IMS TestUser records
 * - Preserves raw roles and common Canvas identifiers when present
 *
 * @param {string} ltiServer - Base URL of the LTI tool server (e.g., https://tool.example)
 * @param {string} jwt - LTI bearer token to authorise the NRPS call
 * @returns {Promise<MemberDetails[]>} Promise resolving to normalized member objects
 */
export function fetchMembers(ltiServer, jwt) {
	return fetch(ltiServer + '/nrps/', {
		headers: {
			'Accept': 'application/json',
			'Authorization': 'Bearer ' + jwt
		}
	})
        .then(response => {
			if (response.ok) {
				return response.json()
			}
			throw new Error('Bad response: ' + response.status)
		})
        .then(json => {
            return json.members.map(member => {
                const details = {
                    name: member.name,
                    email: member.email,
                    first_name: member.given_name,
                    last_name: member.family_name,
                    roles: member.roles
                }

                // Safely access LTI custom claim using optional chaining; default to empty object
                const custom = member.message?.[0]?.['https://purl.imsglobal.org/spec/lti/claim/custom'] || {}
                const sis_integration_id = (custom.canvas_user_sis_integration_id === '$Canvas.user.sisIntegrationId') ? null : custom.canvas_user_sis_integration_id
                const sis_source_id = (custom.canvas_user_sis_source_id === '$Canvas.user.sisSourceId') ? null : custom.canvas_user_sis_source_id

                // TODO will need to deal with somebody having multiple roles
                const membership_roles = (custom.canvas_membership_roles === '$Canvas.membership.roles') ? null : custom.canvas_membership_roles

                Object.assign(details, {
                    id: custom.canvas_user_id,
                    login_id: custom.canvas_user_login_id,
                    sis_integration_id,
                    sis_source_id,
                    membership_roles

                })

                return details
            }).filter(
                // We don't want to include anyone who is a test user.
                details => !details.roles.includes('http://purl.imsglobal.org/vocab/lti/system/person#TestUser')
            )
        })
}
